/**
 * psmsPayloadUtils.ts
 *
 * Shared utility for building the common PSMS API request payload from psmsState.
 * psmsState is the Redux slice set by PSMS_INIT (via getPsmsState selector).
 *
 * Used by:
 *  - psmsSaga.ts (saga workers — no React hooks)
 *  - ExpandableCardSection.tsx (useMemo in component)
 *  - Any future child page that needs to POST to a PSMS endpoint
 */

import { checkIsExternal, checkIsSimulationMode } from '@src/utility/createStorage';
export interface PsmsApiPayload {
  IsPSMS: string;
  ProviderSysKey: string;
  TimeFilter: string;
  Lob?: string;
  EntityId?: string;
  Tins?: string;
  Region?: string;
  State?: string;
  Market?: string[];
  isElite?: string;
  PamIndicator?: string;
  [key: string]: unknown; // allow callers to spread additional fields
}

/**
 * Flat psmsState shape produced by buildPsmsStateFromFilterState / PSMS_INIT.
 */
export interface PsmsState {
  currentFlow?: string;
  timeFilter?: string;
  lob?: string;
  entityId?: string;
  taxId?: string[];
  region?: string;
  state?: string[];
  market?: string[];
  program?: string[];
}

/**
 * Helper function to get TIN from sessionStorage for HCO flow in simulation mode
 */
const getHcoSimulationTin = (): string | null => {
  try {
    const state = sessionStorage.getItem('state');
    if (state) {
      const parsedState = JSON.parse(state);
      return (
        parsedState?.selectedCorporateTin?.value ||
        parsedState?.uhc?.selectedCorporateTin?.value ||
        null
      );
    }
  } catch (error) {
    console.warn('Failed to get HCO simulation TIN:', error);
  }
  return null;
};

const STANDARD_TIME_PERIODS = ['Last30Days', 'Last3Months', 'Last6Months', 'Last12Months', 'YTD'];

const resolveTimeFilter = (timeFilter?: string): string => {
  const value = timeFilter || 'Last12Months';
  return STANDARD_TIME_PERIODS.includes(value) ? value : 'CalendarYear';
};

const resolveLob = (lob?: string): string | undefined => (lob && lob !== 'All' ? lob : undefined);

const filterValidValues = (values?: string[]): string[] =>
  (values || []).filter((v) => v && v !== 'All');

// Region/State/Market/Program fields — Geo flow only (FR6)
const buildGeoFields = (psmsState: PsmsState): Partial<PsmsApiPayload> => {
  const fields: Partial<PsmsApiPayload> = {};

  const region = psmsState?.region;
  if (region && region !== 'All Regions') {
    fields.Region = region;
  }

  const states = filterValidValues(psmsState?.state);
  if (states.length > 0) {
    fields.State = states.join(',');
  }

  const markets = filterValidValues(psmsState?.market);
  if (markets.length > 0) {
    fields.Market = markets;
  }

  if (psmsState?.program?.includes('Elite Advantage')) {
    fields.isElite = 'Y';
  }
  if (psmsState?.program?.includes('PAM')) {
    fields.PamIndicator = 'Y';
  }

  return fields;
};

const taxIdTins = (psmsState: PsmsState): string | undefined =>
  filterValidValues(psmsState?.taxId).join(',') || undefined;

// Mirrors the original two-pass precedence: HCO-simulation/taxId first, then
// the external-user check runs afterwards and may override the result again.
const resolveTins = (
  psmsState: PsmsState,
  isHcoFlow: boolean,
  isSimulationMode: boolean
): string | undefined => {
  let tins: string | undefined;

  if (isHcoFlow && isSimulationMode) {
    const taxId = taxIdTins(psmsState);
    if (taxId) {
      tins = taxId;
    } else {
      const hcoTin = getHcoSimulationTin();
      if (hcoTin) tins = hcoTin;
    }
  } else {
    const taxId = taxIdTins(psmsState);
    if (taxId) tins = taxId;
  }

  if (checkIsExternal()) {
    const externalTin = getHcoSimulationTin();
    if (externalTin) tins = externalTin;
  } else {
    const taxId = taxIdTins(psmsState);
    if (taxId) tins = taxId;
  }

  return tins;
};

const buildTinFlowFields = (
  psmsState: PsmsState,
  isHcoFlow: boolean,
  isSimulationMode: boolean
): Partial<PsmsApiPayload> => {
  const fields: Partial<PsmsApiPayload> = {};

  if (psmsState?.entityId && !isSimulationMode) {
    fields.EntityId = psmsState.entityId;
  }

  const tins = resolveTins(psmsState, isHcoFlow, isSimulationMode);
  if (tins) {
    fields.Tins = tins;
  }

  return fields;
};

/**
 * Builds the base PSMS API payload from the flat psmsState object.
 *
 * Geo flow  (currentFlow === 'GEO_FLOW'):  Region / State / Market
 * TIN flow  (everything else):             EntityId / Tins
 * HCO flow in simulation mode:             Tins from sessionStorage.uhc.selectedCorporateTin.value
 *
 * @param psmsState - value returned by getPsmsState selector
 * @param overrides - optional extra fields merged on top (e.g. ClaimsBy, Type…)
 */
export const buildPsmsApiPayload = (
  psmsState: PsmsState,
  overrides: Record<string, unknown> = {}
): PsmsApiPayload => {
  const isGeoFlow = psmsState?.currentFlow === 'GEO_FLOW';
  const isHcoFlow = psmsState?.currentFlow === 'HCO_FLOW';
  const isSimulationMode = checkIsSimulationMode();

  const base: PsmsApiPayload = {
    IsPSMS: '1',
    ProviderSysKey: '',
    TimeFilter: resolveTimeFilter(psmsState?.timeFilter),
    TimeFilterText: psmsState?.timeFilter || '',
    Lob: resolveLob(psmsState?.lob),
  };

  const flowFields = isGeoFlow
    ? buildGeoFields(psmsState)
    : buildTinFlowFields(psmsState, isHcoFlow, isSimulationMode);

  return { ...base, ...flowFields, ...overrides };
};
